import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { cn, formatBytes } from "@/lib/utils";

const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB — Free tier limit per PRD §5
const ACCEPTED = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/pdf": [".pdf"],
};

interface DropzoneProps {
  onFile: (file: File) => void;
}

export function Dropzone({ onFile }: DropzoneProps) {
  const onDrop = useCallback(
    (
      accepted: File[],
      rejected: ReadonlyArray<{ file: File; errors: ReadonlyArray<{ code: string; message: string }> }>,
    ) => {
      if (rejected.length > 0) {
        const first = rejected[0];
        const error = first.errors[0];
        if (error.code === "file-too-large") {
          toast.error(`File too large (${formatBytes(first.file.size)}). Max ${formatBytes(MAX_SIZE_BYTES)} on the free tier.`);
        } else if (error.code === "file-invalid-type") {
          toast.error("Only JPG, PNG, and PDF files are supported.");
        } else {
          toast.error(error.message);
        }
        return;
      }
      if (accepted.length > 0) {
        onFile(accepted[0]);
      }
    },
    [onFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED,
    maxSize: MAX_SIZE_BYTES,
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        "border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors",
        "bg-white hover:border-brand hover:bg-brand-50/50",
        isDragActive ? "border-brand bg-brand-50" : "border-neutral-300",
      )}
    >
      <input {...getInputProps()} />
      <Upload className={cn("h-10 w-10 mx-auto mb-4", isDragActive ? "text-brand" : "text-neutral-400")} />
      <p className="text-base font-medium text-neutral-700">
        {isDragActive ? "Drop it here" : "Drag a document here, or click to select"}
      </p>
      <p className="text-sm text-neutral-500 mt-1">JPG · PNG · PDF · Max 5 MB</p>
    </div>
  );
}
